import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import Icon from '../Icon';
import CarouselNav from './CarouselNav';
import CarouselThumbs from './CarouselThumbs';

const CarouselFullscreen = ({
    classes,
    currentIndex,
    images,
    onClose,
    onNext,
    onPrevious,
    onSelectSlide,
    placeholder,
}) => {
    const [imageError, setImageError] = useState(false);
    const imagesCount = images?.length || 0;
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < imagesCount - 1;

    const slidesStyle = useMemo(
        () => ({
            transform: `translateX(-${currentIndex * 100}%)`,
        }),
        [currentIndex],
    );

    const handleImageError = useCallback(() => {
        if (placeholder && !imageError) {
            setImageError(true);
        }
    }, [placeholder, imageError]);

    useEffect(() => {
        setImageError(false);
    }, [currentIndex]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    if (hasPrevious) onPrevious();
                    break;
                case 'ArrowRight':
                    if (hasNext) onNext();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [onClose, onPrevious, onNext, hasPrevious, hasNext]);

    const content = (
        <div className={classes.fullscreenOverlay}>
            <div className={classes.fullscreenContent}>
                <button type="button" className={classes.fullscreenClose} onClick={onClose}>
                    <Icon name="close" size="large" color="white" />
                </button>

                <div className={classes.fullscreenSlideWrapper}>
                    <div className={classes.fullscreenSlidesTrack} style={slidesStyle}>
                        {images.map((image, index) => {
                            const imageSrc =
                                imageError && index === currentIndex
                                    ? placeholder
                                    : image?.original;
                            return (
                                <div key={index} className={classes.fullscreenSlide}>
                                    <img
                                        className={classes.fullscreenImage}
                                        src={imageSrc}
                                        alt=""
                                        onError={handleImageError}
                                        draggable={false}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    <CarouselNav
                        direction="left"
                        disabled={!hasPrevious}
                        onClick={onPrevious}
                        classes={classes}
                    />
                    <CarouselNav
                        direction="right"
                        disabled={!hasNext}
                        onClick={onNext}
                        classes={classes}
                    />
                </div>

                {imagesCount > 1 && (
                    <CarouselThumbs
                        images={images}
                        currentIndex={currentIndex}
                        onSelect={onSelectSlide}
                        placeholder={placeholder}
                        classes={classes}
                    />
                )}
            </div>
        </div>
    );

    return createPortal(content, document.body);
};

CarouselFullscreen.propTypes = {
    classes: PropTypes.object.isRequired,
    currentIndex: PropTypes.number.isRequired,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            original: PropTypes.string,
            thumbnail: PropTypes.string,
        }),
    ).isRequired,
    onClose: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onPrevious: PropTypes.func.isRequired,
    onSelectSlide: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
};

export default memo(CarouselFullscreen);
